import { api, type UserCreditTransactionVo } from "@/api";
import { formatUtcToLocal } from "@/util/datetime";
import { useEffect, useState } from "react";

export type CreditTransactionMode = "thread" | "account";

export interface UseCreditTransactionsParams {
  mode: CreditTransactionMode;
  threadId?: string;
  pageNum?: number;
  pageSize?: number;
  enabled?: boolean;
}

export interface UseCreditTransactionsResult {
  loading: boolean;
  data: UserCreditTransactionVo[];
  total: number;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCreditTransactions({
  mode,
  threadId,
  pageNum = 1,
  pageSize = 10,
  enabled = true,
}: UseCreditTransactionsParams): UseCreditTransactionsResult {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UserCreditTransactionVo[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    // Don't fetch if disabled
    if (!enabled) {
      return;
    }

    // Validate threadId for thread mode
    if (mode === "thread" && !threadId) {
      setData([]);
      setTotal(0);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let res;
      if (mode === "thread") {
        res = await api.credits.getUserCreditTransactionByThread(
          {
            threadId: threadId!,
            pageNum,
            pageSize,
          },
          {},
        );
      } else {
        res = await api.credits.getUserAllCreditTransaction(
          {
            pageNum,
            pageSize,
          },
          {},
        );
      }

      if (res.data) {
        const list = (res.data.list || []).map((item) => ({
          ...item,
          createTime: item.createTime
            ? formatUtcToLocal(item.createTime)
            : item.createTime,
        }));
        setData(list);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Failed to fetch credit transactions");
      console.error("Failed to fetch credit transactions:", error);
      setError(error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      void fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, threadId, pageNum, pageSize, enabled]);

  return {
    loading,
    data,
    total,
    error,
    refetch: fetchData,
  };
}
